import React, { FC, useMemo, useState } from 'react'

import { Location, useLocation, useSearchParams } from 'react-router-dom'

import {
  Stack,
  Text,
  Surface,
  Element,
  Link,
  LinkText,
  EmojiIcon,
} from '@components'

import { SignInScreenTokenForm } from './SignInScreenTokenForm'
import { SignInScreenEmailPasswordForm } from './SignInScreenEmailPasswordForm'

export const SignInScreenContent: FC = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [inputEmail, setInputEmail] = useState('')

  const signInMethod = useMemo<'email' | 'password'>(() => {
    const method = searchParams.get('method')

    return ['email', 'password'].includes(method || '')
      ? (method as 'email' | 'password')
      : 'email'
  }, [searchParams.get('method')])

  const routerState = useMemo(
    () => ({
      state: {
        backgroundLocation: location.state?.backgroundLocation as
          | Location
          | undefined,
        authRedirectPath: location.state?.authRedirectPath as string,
      },
    }),
    [location]
  )

  return (
    <Stack gap={6}>
      <Element
        tokens={{
          paddingHorizontal: 8,
          paddingTop: 8,
        }}
      >
        <Stack gap={8}>
          <Stack direction='horizontal' gap={4} alignItems='center'>
            <Surface variant='formIcon'>
              <EmojiIcon size={5} name='waving-hand' />
            </Surface>

            <Stack gap={1} expand>
              <Text size={6} variant='heading'>
                Sign in
              </Text>

              {signInMethod === 'email' ? (
                <Text size={4} variant='label'>
                  We'll send you a code securely via email
                </Text>
              ) : (
                <Text size={4} variant='label'>
                  Enter your email and password below
                </Text>
              )}
            </Stack>
          </Stack>

          <SignInForm
            initialEmail={inputEmail}
            setEmail={setInputEmail}
            method={signInMethod}
            authRedirectPath={routerState.state?.authRedirectPath}
          />
        </Stack>
      </Element>

      <Surface variant='roundedBottom'>
        {signInMethod === 'email' ? (
          <Text textAlign='center' size={4} variant='onSurface'>
            Prefer a password?{' '}
            <Link to='/sign-in?method=password' options={routerState}>
              <LinkText size={4} variant='onSurface'>
                Use a password instead
              </LinkText>
            </Link>
          </Text>
        ) : (
          <Text textAlign='center' size={4} variant='onSurface'>
            Prefer an email?{' '}
            <Link to='/sign-in?method=email' options={routerState}>
              <LinkText size={4} variant='onSurface'>
                Use an email instead
              </LinkText>
            </Link>
          </Text>
        )}
      </Surface>
    </Stack>
  )
}

const SignInForm: FC<{
  initialEmail?: string
  setEmail?: (email: string) => any
  method: 'email' | 'password'
  authRedirectPath: string
}> = ({ initialEmail, setEmail, method, authRedirectPath }) => {
  if (method === 'email') {
    return (
      <SignInScreenTokenForm
        initialEmail={initialEmail}
        setEmail={setEmail}
        authRedirectPath={authRedirectPath}
      />
    )
  }

  return (
    <SignInScreenEmailPasswordForm
      initialEmail={initialEmail}
      setEmail={setEmail}
    />
  )
}

export default SignInScreenContent
